import { Controller } from 'stimulus';

// Cascading template filters on the create-job wizard's first step.
//
// The server derives the guided questions from the account's
// TemplateQuestions (see TemplatePickerFilters) and hands this controller
// two JSON payloads: the ordered questions and a template-id -> question ->
// answers map. Questions render as labeled rows of toggle chips above the
// template dropdown; picking a chip narrows the dropdown and reveals the
// next question. A question's row renders whenever the currently filtered
// pool has at least one matching option — so a question that only applies
// to one slice of the catalog asks itself exactly there. A REQUIRED
// question that is showing but unanswered locks the dropdown ("required
// when asked" — if it has nothing to offer in the current slice it never
// renders, so it can never block anyone).
export default class extends Controller {
  static targets = ['rows', 'select'];

  connect() {
    this.levels = JSON.parse(this.data.get('levels'));
    this.facets = JSON.parse(this.data.get('facets'));
    this.selected = {};
    this.allOptions = Array.from(this.selectTarget.options)
      .filter(option => option.value !== '')
      .map(option => ({ value: option.value, text: option.text }));
    this.render();
  }

  render() {
    const host = this.rowsTarget;
    host.innerHTML = '';

    let pool = this.allOptions;
    let awaitingAnswer = false;
    let locked = false;
    this.levels.forEach(level => {
      if (awaitingAnswer) return;

      const counts = this.valueCounts(pool, level.key);
      const values = Object.keys(counts);
      const selectedValue = this.selected[level.key];
      if (values.length === 0 && !selectedValue) return;

      host.appendChild(this.buildRow(level, counts, selectedValue));

      if (selectedValue) {
        pool = pool.filter(option => this.valuesFor(option, level.key).indexOf(selectedValue) >= 0);
      } else {
        awaitingAnswer = true;
        if (level.required) locked = true;
      }
    });

    if (locked) {
      const note = document.createElement('p');
      note.className = 'text-xs text-gray-500 mb-2';
      note.textContent = 'Answer the question above to choose a template.';
      host.appendChild(note);
    } else if (Object.keys(this.selected).length > 0) {
      host.appendChild(this.buildCountLine(pool));
    }
    this.syncSelect(locked ? [] : pool, locked);
  }

  buildRow(level, counts, selectedValue) {
    const wrap = document.createElement('div');

    const label = document.createElement('p');
    label.className = 'text-sm font-medium text-gray-900 mt-3 mb-1';
    label.textContent = level.name;
    if (level.required) {
      const star = document.createElement('span');
      star.className = 'text-red-600 font-bold ml-px';
      star.textContent = '*';
      label.appendChild(star);
    }
    wrap.appendChild(label);

    const row = document.createElement('div');
    row.className = 'flex flex-wrap items-center';
    const values = Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
    values.forEach(value => {
      const on = selectedValue === value;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'inline-flex items-center px-3 py-1 mr-2 mb-2 rounded-full text-sm border cursor-pointer focus:outline-none ' +
        (on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400');
      chip.textContent = value;
      const count = document.createElement('span');
      count.className = 'ml-1 text-xs ' + (on ? 'text-blue-200' : 'text-gray-400');
      count.textContent = counts[value];
      chip.appendChild(count);
      chip.addEventListener('click', () => this.toggle(level.key, value));
      row.appendChild(chip);
    });
    wrap.appendChild(row);
    return wrap;
  }

  buildCountLine(pool) {
    const line = document.createElement('p');
    line.className = 'text-xs text-gray-500 mb-2';
    line.textContent = `${pool.length} of ${this.allOptions.length} templates match`;

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'ml-2 text-blue-600 cursor-pointer focus:outline-none';
    clear.textContent = 'Clear filters';
    clear.addEventListener('click', () => {
      this.selected = {};
      this.render();
    });
    line.appendChild(clear);
    return line;
  }

  toggle(levelKey, value) {
    if (this.selected[levelKey] === value) {
      delete this.selected[levelKey];
    } else {
      this.selected[levelKey] = value;
    }
    const index = this.levels.findIndex(level => level.key === levelKey);
    this.levels.slice(index + 1).forEach(level => delete this.selected[level.key]);
    this.render();
  }

  valueCounts(pool, levelKey) {
    const counts = {};
    pool.forEach(option => {
      this.valuesFor(option, levelKey).forEach(value => {
        counts[value] = (counts[value] || 0) + 1;
      });
    });
    return counts;
  }

  valuesFor(option, levelKey) {
    const byLevel = this.facets[option.value];
    return (byLevel && byLevel[levelKey]) || [];
  }

  // Rebuilds the dropdown to the filtered pool and applies the required
  // lock. The select is enhanced by TomSelect (select2--select controller);
  // the original select stays in sync, so fall back to rebuilding it
  // directly if TomSelect isn't up yet. Locking also clears any selection —
  // a disabled select doesn't submit, so a stale pick must not linger.
  syncSelect(pool, locked) {
    const select = this.selectTarget;
    const keep = !locked && select.value && pool.some(option => option.value === select.value) ? select.value : '';
    const tomSelect = select.tomselect;
    if (tomSelect) {
      tomSelect.clear(true);
      tomSelect.clearOptions();
      pool.forEach(option => tomSelect.addOption({ value: option.value, text: option.text }));
      tomSelect.refreshOptions(false);
      if (keep) tomSelect.setValue(keep, true);
      if (locked) tomSelect.disable(); else tomSelect.enable();
    } else {
      Array.from(select.options)
        .filter(option => option.value !== '')
        .forEach(option => option.remove());
      pool.forEach(option => {
        select.appendChild(new Option(option.text, option.value, false, option.value === keep));
      });
      if (!keep) select.value = '';
      select.disabled = locked;
    }
  }
}
